
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { formatCurrency } from '@/lib/formatters';
import { BalanceSheetEntry } from '@/lib/balance-sheet-types';
import { BalanceSheetEntryForm } from './BalanceSheetEntryForm';
import { FileUploadDialog } from './FileUploadDialog';
import { FileUploadList } from './FileUploadList';
import { Plus, Edit, Trash2, FileText, Shield, Upload } from 'lucide-react';

export function BalanceSheet() {
  const { user } = useAuth();
  const { canWrite, canRead } = usePermissions();
  const { toast } = useToast();
  
  const [entries, setEntries] = useState<BalanceSheetEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState<BalanceSheetEntry | null>(null);

  useEffect(() => {
    if (canRead) {
      fetchEntries();
    }
  }, [canRead]);

  const fetchEntries = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('balance_sheet_entries')
        .select('*')
        .order('period_end_date', { ascending: false });

      if (error) throw error;
      
      // Type assertion to ensure the data matches our BalanceSheetEntry interface
      const typedEntries = (data || []) as BalanceSheetEntry[];
      setEntries(typedEntries);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (entryId: string) => {
    if (!confirm('Are you sure you want to delete this entry?')) return;

    try {
      const { error } = await supabase
        .from('balance_sheet_entries')
        .delete()
        .eq('id', entryId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Balance sheet entry deleted successfully.",
      });

      fetchEntries();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleEdit = (entry: BalanceSheetEntry) => {
    setEditingEntry(entry);
    setShowForm(true);
  };

  const handleEditComplete = () => {
    setEditingEntry(null);
    setShowForm(false);
  };

  const calculateTotals = () => {
    const assets = entries.filter(e => e.account_type === 'asset').reduce((sum, e) => sum + e.balance, 0);
    const liabilities = entries.filter(e => e.account_type === 'liability').reduce((sum, e) => sum + e.balance, 0);
    const equity = entries.filter(e => e.account_type === 'equity').reduce((sum, e) => sum + e.balance, 0);
    
    return { assets, liabilities, equity };
  };

  const { assets, liabilities, equity } = calculateTotals();
  const balanceCheck = assets - (liabilities + equity);

  if (!canRead) {
    return (
      <Card>
        <CardContent className="pt-6">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertDescription>
              You don't have permission to access the balance sheet.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold tracking-tight">Balance Sheet</h2>
        {canWrite && (
          <div className="flex gap-2">
            <Button onClick={() => setShowUploadDialog(true)} variant="outline">
              <Upload className="h-4 w-4 mr-2" />
              Import File
            </Button>
            <Button onClick={() => setShowForm(!showForm)}>
              <Plus className="h-4 w-4 mr-2" />
              {showForm ? 'Hide Form' : 'Add Entry'}
            </Button>
          </div>
        )}
      </div>

      {/* Balance Check Alert */}
      {Math.abs(balanceCheck) > 0.01 && (
        <Alert variant="destructive">
          <AlertDescription>
            Balance sheet does not balance! Assets - (Liabilities + Equity) = {formatCurrency(balanceCheck)}
          </AlertDescription>
        </Alert>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Assets</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{formatCurrency(assets)}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Liabilities</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{formatCurrency(liabilities)}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Equity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{formatCurrency(equity)}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Balance Check</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${Math.abs(balanceCheck) < 0.01 ? 'text-green-600' : 'text-red-600'}`}>
              {Math.abs(balanceCheck) < 0.01 ? '✓ Balanced' : formatCurrency(balanceCheck)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Entry Form */}
      {showForm && canWrite && (
        <BalanceSheetEntryForm
          onEntryAdded={() => {
            fetchEntries();
            if (!editingEntry) setShowForm(false);
          }}
          editingEntry={editingEntry}
          onEditComplete={handleEditComplete}
        />
      )}

      {/* File Upload List */}
      {canRead && (
        <FileUploadList onRefresh={fetchEntries} />
      )}

      {/* Entries Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Balance Sheet Entries
          </CardTitle>
        </CardHeader>
        <CardContent>
          {entries.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No balance sheet entries found. {canWrite && 'Add your first entry to get started.'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Balance</TableHead>
                  <TableHead>Period End</TableHead>
                  <TableHead>Notes</TableHead>
                  {canWrite && <TableHead>Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="font-medium">{entry.account_name}</TableCell>
                    <TableCell>
                      <Badge variant={
                        entry.account_type === 'asset' ? 'default' : 
                        entry.account_type === 'liability' ? 'destructive' : 'secondary'
                      }>
                        {entry.account_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {entry.account_category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </TableCell>
                    <TableCell className={`font-mono ${entry.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(entry.balance)}
                    </TableCell>
                    <TableCell>{new Date(entry.period_end_date).toLocaleDateString()}</TableCell>
                    <TableCell className="max-w-xs truncate" title={entry.notes || ''}>
                      {entry.notes || '-'}
                    </TableCell>
                    {canWrite && (
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(entry)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(entry.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
              <TableCaption>
                Balance Sheet as of latest period end dates
              </TableCaption>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <FileUploadDialog
        open={showUploadDialog}
        onOpenChange={setShowUploadDialog}
        onUploadComplete={fetchEntries}
      />
    </div>
  );
}
